import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Presentation, ArrowRight, Loader2 } from 'lucide-react';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeLabels = {
  7: 'Kansas History', 8: 'U.S. History', 9: 'World History',
  10: 'Modern World', 11: 'U.S. History', 12: 'Government & Economics'
};

// Lesson Plans / Lecture Presentations index — lazy-loads per grade like PrimarySourcesIndex.
export default function LectureIndex() {
  const navigate = useNavigate();
  const [unitsByGrade, setUnitsByGrade] = useState({});
  const [loadedGrades, setLoadedGrades] = useState(new Set());
  const [activeGrade, setActiveGrade] = useState(7);
  const [loadingGrade, setLoadingGrade] = useState(true);

  const loadGrade = useCallback(async (grade) => {
    if (loadedGrades.has(grade)) return;
    setLoadingGrade(true);
    try {
      const res = await axios.get(`${API}/units/grade/${grade}`);
      setUnitsByGrade(prev => ({ ...prev, [grade]: res.data || [] }));
      setLoadedGrades(prev => new Set(prev).add(grade));
    } catch (_e) {
      setUnitsByGrade(prev => ({ ...prev, [grade]: [] }));
      setLoadedGrades(prev => new Set(prev).add(grade));
    } finally {
      setLoadingGrade(false);
    }
  }, [loadedGrades]);

  useEffect(() => { loadGrade(activeGrade); }, [activeGrade, loadGrade]);

  const units = unitsByGrade[activeGrade] || [];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="lecture-index">
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Resources</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight mb-2">Lesson Plans & Presentations</h1>
          <p className="text-sm text-[#5A6577] max-w-2xl">Interactive slideshow presentations and lecture notes — organized by grade level and unit.</p>
        </div>

        {/* Grade tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[7, 8, 9, 10, 11, 12].map(g => (
            <button
              key={g}
              onClick={() => setActiveGrade(g)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeGrade === g ? 'bg-[#14213D] text-[#FDFBF7]' : 'bg-white text-[#2D3748] hover:bg-[#F9F8F4] border border-[#EBE7E0]'}`}
              data-testid={`grade-tab-${g}`}
            >
              Grade {g}: {gradeLabels[g]}
            </button>
          ))}
        </div>

        {loadingGrade && !loadedGrades.has(activeGrade) ? (
          <div className="flex flex-col items-center py-20 text-[#5A6577]">
            <Loader2 className="h-6 w-6 animate-spin mb-3" />
            <p className="text-sm">Loading lectures…</p>
          </div>
        ) : units.length === 0 ? (
          <div className="text-center py-16 bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg">
            <Presentation className="h-10 w-10 text-[#E2E8F0] mx-auto mb-3" />
            <h3 className="font-serif-display text-lg text-[#14213D] mb-1">No lectures yet</h3>
            <p className="text-sm text-[#5A6577]">Add lecture notes to units to populate this view.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {units.map((unit, i) => (
              <article
                key={unit.id}
                onClick={() => navigate(`/lecture-notes/${unit.id}`)}
                className="group cursor-pointer bg-white border border-[#EBE7E0] rounded-md p-5 hover:-translate-y-1 hover:shadow-md hover:border-[#14213D]/20 transition-all relative overflow-hidden"
                data-testid={`lecture-card-${i}`}
              >
                <div className="absolute top-0 left-0 w-full h-0.5 bg-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-[#D4AF37] font-semibold">Unit {unit.unit_number}</span>
                  <Presentation className="h-4 w-4 text-[#7A93AC]" />
                </div>
                <h3 className="font-serif-display text-base text-[#14213D] leading-tight mb-2 line-clamp-2">{unit.title}</h3>
                <p className="text-xs text-[#5A6577] line-clamp-2 leading-relaxed mb-4">
                  {unit.lecture_notes?.length ? `${unit.lecture_notes.length} lecture segments available` : 'Lecture content available'}
                </p>
                <span className="text-xs font-medium text-[#14213D] inline-flex items-center gap-1 group-hover:text-[#D4AF37] transition-colors">
                  Open presentation <ArrowRight className="h-3 w-3" />
                </span>
              </article>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
